import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Sparkles, Target, Clock, BarChart3, TrendingUp,
  ChevronRight, ArrowRight, Loader2, Building2
} from 'lucide-react';
import api from '../../lib/api';

const PRIORITY_STYLES = {
  high: { bg: 'bg-red-50 dark:bg-red-950/20', border: 'border-red-200/50 dark:border-red-800/30', dot: 'bg-red-500', text: 'text-red-700 dark:text-red-400' },
  medium: { bg: 'bg-amber-50 dark:bg-amber-950/20', border: 'border-amber-200/50 dark:border-amber-800/30', dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-400' },
  info: { bg: 'bg-sky-50 dark:bg-sky-950/20', border: 'border-sky-200/50 dark:border-sky-800/30', dot: 'bg-sky-500', text: 'text-sky-700 dark:text-sky-400' },
};

const TYPE_ICONS = {
  hot_lead: Target,
  idle_leads: Clock,
  pipeline_summary: BarChart3,
  industry_insight: TrendingUp,
  ai_recommendation: Sparkles,
};

export default function BrainSuggestions() {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);

    const fetchSuggestions = async () => {
      try {
        const res = await api.get('/api/intelligence/dashboard-suggestions', { signal: controller.signal });
        setSuggestions(res.data?.suggestions || []);
      } catch {
        // Silent fail — timeout or network error
      } finally {
        clearTimeout(timeout);
        setLoading(false);
      }
    };
    fetchSuggestions();

    return () => { clearTimeout(timeout); controller.abort(); };
  }, []);

  const handleAction = (s) => {
    if (s.lead_id) navigate(`/leads/${s.lead_id}`);
    else if (s.lead_ids?.length) navigate('/leads');
    else if (s.action === 'view_pipeline') navigate('/leads');
    else if (s.action === 'discover') navigate('/discover-leads');
    else if (s.action === 'smart') {
      const text = (s.description || '').toLowerCase();
      if (text.includes('discover') || text.includes('import') || text.includes('vertical') || text.includes('industry')) navigate('/discover-leads');
      else navigate('/leads');
    }
  };

  if (loading) {
    return (
      <div className="mt-6 animate-[fadeUp_0.4s_ease-out_0.3s_both]" data-testid="brain-suggestions-loading">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-3.5 w-3.5 text-cyan-500" />
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Brain Insights</span>
        </div>
        <div className="relative overflow-hidden rounded-xl border border-violet-200/40 dark:border-violet-800/20 bg-gradient-to-r from-violet-50/80 via-cyan-50/50 to-blue-50/80 dark:from-violet-950/20 dark:via-cyan-950/10 dark:to-blue-950/20 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-100 to-cyan-100 dark:from-violet-900/40 dark:to-cyan-900/40 flex items-center justify-center shrink-0">
              <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-400 animate-pulse" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-violet-700 dark:text-violet-300">AI is analyzing your pipeline</p>
              <p className="text-xs text-muted-foreground mt-0.5">Generating personalized insights and recommendations...</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <div className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
          {/* Animated shimmer bar */}
          <div className="mt-3 h-1 rounded-full overflow-hidden bg-violet-200/30 dark:bg-violet-800/20">
            <div className="h-full w-1/3 rounded-full animate-[shimmer_1.5s_ease-in-out_infinite]" style={{ background: 'linear-gradient(90deg, #8b5cf6, #06b6d4, #3b82f6)' }} />
          </div>
        </div>
        <style>{`@keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }`}</style>
      </div>
    );
  }

  if (!suggestions.length) return null;

  return (
    <div className="mt-6 animate-[fadeUp_0.4s_ease-out_0.3s_both]" data-testid="brain-suggestions">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-3.5 w-3.5 text-cyan-500" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Brain Insights</span>
      </div>

      <div className="space-y-2">
        {suggestions.map((s, i) => {
          const isAI = s.type === 'ai_recommendation';
          const style = isAI
            ? { bg: 'bg-gradient-to-r from-violet-50 to-cyan-50 dark:from-violet-950/20 dark:to-cyan-950/20', border: 'border-violet-200/50 dark:border-violet-800/30', text: 'text-violet-700 dark:text-violet-400' }
            : (PRIORITY_STYLES[s.priority] || PRIORITY_STYLES.info);
          const Icon = TYPE_ICONS[s.type] || Sparkles;

          return (
            <button
              key={i}
              onClick={() => handleAction(s)}
              className={`w-full text-left group flex items-start gap-3 p-3.5 rounded-xl border ${style.border} ${style.bg} hover:shadow-sm transition-all`}
              data-testid={`brain-suggestion-${s.type}-${i}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isAI ? 'bg-gradient-to-br from-violet-100 to-cyan-100 dark:from-violet-900/40 dark:to-cyan-900/40' : style.bg}`}>
                <Icon className={`h-4 w-4 ${style.text}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${style.text}`}>{isAI ? '' : s.title}</p>
                <p className={`text-xs ${isAI ? 'text-foreground/80 font-medium' : 'text-muted-foreground'} mt-0.5 leading-relaxed`}>{s.description}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-foreground/50 shrink-0 mt-1 transition-colors" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
